import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';

export default function KnowledgeGapsList() {
  const navigate = useNavigate();
  const since7d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  const { data: gaps = [], isLoading } = useQuery({
    queryKey: ['admin-knowledge-gaps-flags'],
    queryFn: async () => {
      // Primary source: posit_task_flags created in the last 7 days
      const { data: flags } = await supabase
        .from('posit_task_flags')
        .select('id, posit_id, user_id, question, context, status, processed, created_at, updated_at, reason')
        .gte('created_at', since7d)
        .order('created_at', { ascending: false });

      // Fetch posit titles for display
      const positIds = Array.from(new Set((flags || []).map(f => f.posit_id)));
      let positMap: Record<string, string> = {};
      if (positIds.length) {
        const { data: posits } = await supabase
          .from('posits')
          .select('id, title')
          .in('id', positIds);
        (posits || []).forEach((p: any) => { positMap[p.id] = p.title || 'Untitled Posit'; });
      }

      return (flags || []).map((f: any) => ({
        ...f,
        posit_title: positMap[f.posit_id] || 'Untitled Posit'
      }));
    }
  });

  const openCount = useMemo(() => gaps.filter((g: any) => (g.status ?? 'open') === 'open' && !g.processed).length, [gaps]);

  return (
    <DashboardLayout title="Knowledge Gaps" description="Gaps created from uncertain or low‑evidence answers (last 7 days)." maxWidth="7xl">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-muted-foreground">Open gaps: {openCount} • Total: {gaps.length}</div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-4 space-y-2">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Posit</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Question</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {gaps.map((g: any) => (
              <TableRow key={g.id}>
                <TableCell className="whitespace-nowrap">{new Date(g.created_at).toLocaleString()}</TableCell>
                <TableCell className="truncate max-w-[260px]" title={g.posit_title}>{g.posit_title}</TableCell>
                <TableCell className="capitalize text-xs">{g.reason || 'no_citations'}</TableCell>
                <TableCell className="text-sm max-w-[520px] truncate" title={g.question}>{g.question}</TableCell>
                <TableCell>
                  <Badge variant={(!g.processed && (g.status ?? 'open') === 'open') ? 'default' : 'outline'} className="capitalize">
                    {(g.status ?? (g.processed ? 'closed' : 'open'))}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="secondary" size="sm" onClick={() => navigate(`/posit-tasks/${g.id}`)}>Review</Button>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && gaps.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">No gaps created in the last 7 days</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        )}
      </div>
    </DashboardLayout>
  );
}


